import {useMutation, useQuery} from '@tanstack/react-query';
import {
  AppService,
  Employee,
  EmployeeService,
  EmployeeWithDelegates,
  MnpiAudiencesService,
  MnpiMetricSearchResult,
  MnpiMetricWithAudience,
  ScopeType,
  UserService,
  Workstream,
  WorkstreamListService,
  WorkstreamService,
} from 'client';
import {useDebouncedValue} from 'hooks/useDebounce';
import {useMemo} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';
import {useDefaultWorkstreamTimeframe} from 'views/workstreams/detail/hooks';

export const useDelegateService = () => {
  const {data: employees} = useQuery({
    queryKey: ['delegates'],
    queryFn: () => getService(UserService).getDelegatesApiV1DelegatesAdminGet(),
  });

  const {mutateAsync: updateEmployeeDelegate} = useMutation({
    mutationFn: ({employee}: {employee: EmployeeWithDelegates}) => {
      return getService(UserService).setDelegatesAdminApiV1DelegatesAdminPost(employee);
    },
    onSettled: (response) => {
      if (!response) {
        return;
      }
      queryClient.invalidateQueries({queryKey: ['delegates']});
    },
  });

  return {employees, updateEmployeeDelegate};
};

export const useExternalAppService = () => {
  const {data: apps} = useQuery({
    queryKey: ['apps'],
    queryFn: () => getService(AppService).listAppsApiV1AppGet(),
  });

  const {mutateAsync: createApp} = useMutation({
    mutationFn: ({name, permissions}: {name: string; permissions: string[]}) => {
      return getService(AppService).createApiV1AppPost(name, permissions);
    },
    onSettled: (response) => {
      if (!response) {
        return;
      }
      queryClient.invalidateQueries({queryKey: ['apps']});
    },
  });

  return {apps, createApp};
};

const MNPI_IDS_KEY = ['mnpi'] as const;
const MNPI_METRICS_KEY = ['mnpi', 'metrics'] as const;
const MNPI_METRIC_SEARCH_KEY = ['mnpi', 'metric-search'] as const;
const MNPI_WORKSTREAM_PILLARS_KEY = ['mnpi', 'workstream-pillars'] as const;

const useNarrativePillarsForWorkstreams = (workstreamIds: number[]) => {
  const timeframe = useDefaultWorkstreamTimeframe();
  const requestedWorkstreamIds = useMemo(() => new Set(workstreamIds), [workstreamIds]);

  return useQuery({
    queryKey: [...MNPI_WORKSTREAM_PILLARS_KEY, timeframe, workstreamIds],
    queryFn: async () => {
      return getService(
        WorkstreamListService
      ).getPrototypeNavTreeApiV1WorkstreamPrototypeNavTreeGet(ScopeType.COMPANY);
    },
    select: (response) => {
      // Company nav-tree pillars come exclusively from the current narrative dependency
      // hierarchy (the same source as Pillar Admin), never from Workstream.pillar.
      return new Map(
        (response.nodes ?? [])
          .filter(
            (workstream): workstream is typeof workstream & {pillar: string} =>
              requestedWorkstreamIds.has(workstream.id) && Boolean(workstream.pillar)
          )
          .map((workstream) => [workstream.id, workstream.pillar])
      );
    },
    enabled: workstreamIds.length > 0 && Boolean(timeframe),
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
  });
};

export type MnpiMetricAudience = MnpiMetricWithAudience & {
  audienceEmployees: Employee[];
  workstreams: Array<Pick<Workstream, 'id' | 'name'>>;
  pillars: string[];
};

export const useMNPIService = () => {
  const {
    data: userIds = [],
    isError: isIdsError,
    isLoading: isIdsLoading,
  } = useQuery({
    queryKey: MNPI_IDS_KEY,
    queryFn: () =>
      getService(WorkstreamService).listMnpiAllowedApiV1WorkstreamMnpiAllowedEmployeesGet(),
  });

  const {
    data: allowedEmployees = [],
    isError: isEmployeesError,
    isLoading: isEmployeesLoading,
  } = useQuery({
    queryKey: ['mnpi', 'employees', userIds],
    queryFn: async () => {
      if (!userIds.length) return [];
      const employeePromises = userIds.map((userId) =>
        getService(EmployeeService).getProfileApiV1PeopleUserIdGet(userId)
      );
      const profiles = await Promise.all(employeePromises);
      return profiles.map((profile) => profile.employee);
    },
    enabled: userIds.length > 0,
    placeholderData: (previousData) => previousData,
  });

  const invalidateMnpi = () => queryClient.invalidateQueries({queryKey: MNPI_IDS_KEY});

  const {mutateAsync: addEmployeeMutation} = useMutation({
    mutationFn: (userId: string) =>
      getService(WorkstreamService).addMnpiAllowedApiV1WorkstreamMnpiAllowedEmployeesPost({
        user_id: userId,
      }),
    onSettled: invalidateMnpi,
  });

  const {mutateAsync: removeEmployeeMutation} = useMutation({
    mutationFn: (userId: string) =>
      getService(
        WorkstreamService
      ).removeMnpiAllowedApiV1WorkstreamMnpiAllowedEmployeesUserIdDelete(userId),
    onSettled: invalidateMnpi,
  });

  const addEmployee = async (employee: Employee) => {
    await addEmployeeMutation(employee.user_id);
  };

  const removeEmployee = async (userId: string) => {
    await removeEmployeeMutation(userId);
  };

  return {
    allowedEmployees,
    addEmployee,
    removeEmployee,
    isError: isIdsError || isEmployeesError,
    isLoading: isIdsLoading || isEmployeesLoading,
  };
};

export const useMNPIMetricSearch = (input: string) => {
  const debouncedInput = useDebouncedValue(input.trim(), 250) as string;
  const {
    data: metrics = [],
    isError,
    isLoading,
  } = useQuery({
    queryKey: [...MNPI_METRIC_SEARCH_KEY, debouncedInput],
    queryFn: () =>
      getService(MnpiAudiencesService).searchMetricsForMnpiApiV1WorkstreamMnpiMetricsSearchGet(
        debouncedInput
      ),
    enabled: debouncedInput.length > 0,
  });

  return {
    metrics: metrics as MnpiMetricSearchResult[],
    isError,
    isLoading,
    query: debouncedInput,
  };
};

export const useMNPIMetricsAudienceService = () => {
  const {
    data: metrics = [],
    isError: isMetricsError,
    isLoading: isMetricsLoading,
  } = useQuery({
    queryKey: MNPI_METRICS_KEY,
    queryFn: () =>
      getService(MnpiAudiencesService).listMnpiMetricsWithAudiencesApiV1WorkstreamMnpiMetricsGet(),
  });

  const audienceUserIds = useMemo(
    () => [...new Set(metrics.flatMap((metric) => metric.audience))].sort(),
    [metrics]
  );
  const metricWorkstreamIds = useMemo(
    () =>
      [...new Set(metrics.flatMap((metric) => metric.workstream_ids ?? []))].sort((a, b) => a - b),
    [metrics]
  );
  const {
    data: narrativePillarByWorkstreamId = new Map<number, string>(),
    isLoading: isNarrativePillarsLoading,
  } = useNarrativePillarsForWorkstreams(metricWorkstreamIds);

  const {
    data: audienceEmployees = [],
    isError: isAudienceEmployeesError,
    isLoading: isAudienceEmployeesLoading,
  } = useQuery({
    queryKey: ['mnpi', 'metric-audience-employees', audienceUserIds],
    queryFn: async () => {
      if (!audienceUserIds.length) return [];
      const employeePromises = audienceUserIds.map((userId) =>
        getService(EmployeeService).getProfileApiV1PeopleUserIdGet(userId)
      );
      const profiles = await Promise.all(employeePromises);
      return profiles
        .map((profile) => profile.employee as Employee | undefined)
        .filter((employee): employee is Employee => Boolean(employee));
    },
    enabled: audienceUserIds.length > 0,
    placeholderData: (previousData) => previousData,
  });

  const employeeByUserId = useMemo(
    () => new Map(audienceEmployees.map((employee) => [employee.user_id, employee])),
    [audienceEmployees]
  );

  const {data: metricWorkstreams = [], isLoading: isMetricWorkstreamsLoading} = useQuery({
    queryKey: ['mnpi', 'metric-workstreams', metricWorkstreamIds],
    queryFn: async () => {
      if (!metricWorkstreamIds.length) return [];
      const workstreamService = getService(WorkstreamService);
      const results = await Promise.allSettled(
        metricWorkstreamIds.map((workstreamId) =>
          workstreamService.getApiV1WorkstreamIdGet(workstreamId)
        )
      );

      return results
        .map((result) => (result.status === 'fulfilled' ? result.value : null))
        .filter((workstream): workstream is Workstream => Boolean(workstream))
        .map((workstream) => ({id: workstream.id, name: workstream.name}));
    },
    enabled: metricWorkstreamIds.length > 0,
    placeholderData: (previousData) => previousData,
  });

  const workstreamById = useMemo(
    () => new Map(metricWorkstreams.map((workstream) => [workstream.id, workstream])),
    [metricWorkstreams]
  );

  const metricsWithAudienceEmployees: MnpiMetricAudience[] = useMemo(
    () =>
      metrics.map((metric) => ({
        ...metric,
        pillars: [
          ...new Set(
            (metric.workstream_ids ?? [])
              .map((workstreamId) => narrativePillarByWorkstreamId.get(workstreamId))
              .filter((pillar): pillar is string => Boolean(pillar))
          ),
        ].sort((a, b) => a.localeCompare(b)),
        workstreams: (metric.workstream_ids ?? []).map((workstreamId) => {
          const workstream = workstreamById.get(workstreamId);
          return workstream ?? {id: workstreamId, name: `Workstream #${workstreamId}`};
        }),
        audienceEmployees: metric.audience
          .map((userId) => employeeByUserId.get(userId))
          .filter((employee): employee is Employee => Boolean(employee))
          .sort((a, b) => a.name.localeCompare(b.name)),
      })),
    [employeeByUserId, metrics, narrativePillarByWorkstreamId, workstreamById]
  );

  const invalidateMnpiMetrics = () => queryClient.invalidateQueries({queryKey: MNPI_METRICS_KEY});

  const {mutateAsync: addAudienceMembersMutation} = useMutation({
    mutationFn: ({metricId, userIds}: {metricId: number; userIds: string[]}) =>
      getService(
        MnpiAudiencesService
      ).addToMetricAudienceApiV1WorkstreamMnpiMetricsMetricIdAudiencePost(metricId, {
        user_ids: userIds,
      }),
    onSettled: invalidateMnpiMetrics,
  });

  const {mutateAsync: removeAudienceMemberMutation} = useMutation({
    mutationFn: ({metricId, userId}: {metricId: number; userId: string}) =>
      getService(
        MnpiAudiencesService
      ).removeFromMetricAudienceApiV1WorkstreamMnpiMetricsMetricIdAudienceUserIdDelete(
        metricId,
        userId
      ),
    onSettled: invalidateMnpiMetrics,
  });

  const {mutateAsync: toggleMetricMutation} = useMutation({
    mutationFn: ({metricId, isMnpi}: {metricId: number; isMnpi: boolean}) =>
      getService(MnpiAudiencesService).toggleMetricMnpiApiV1WorkstreamMnpiMetricsMetricIdTogglePost(
        metricId,
        {is_mnpi: isMnpi}
      ),
    onSettled: () => {
      invalidateMnpiMetrics();
      queryClient.invalidateQueries({queryKey: MNPI_METRIC_SEARCH_KEY});
    },
  });

  const updateMetricAudience = async (metric: MnpiMetricAudience, nextEmployees: Employee[]) => {
    const currentUserIds = new Set(metric.audience);
    const nextUserIds = new Set(nextEmployees.map((employee) => employee.user_id));
    const userIdsToAdd = nextEmployees
      .filter((employee) => !currentUserIds.has(employee.user_id))
      .map((employee) => employee.user_id);
    const userIdsToRemove = metric.audience.filter((userId) => !nextUserIds.has(userId));

    if (userIdsToAdd.length === 0 && userIdsToRemove.length === 0) {
      return;
    }

    await Promise.all([
      ...(userIdsToAdd.length
        ? [addAudienceMembersMutation({metricId: metric.metric_id, userIds: userIdsToAdd})]
        : []),
      ...userIdsToRemove.map((userId) =>
        removeAudienceMemberMutation({metricId: metric.metric_id, userId})
      ),
    ]);
  };

  const unmarkMetric = async (metricId: number) => {
    await toggleMetricMutation({metricId, isMnpi: false});
  };

  const markMetric = async (metricId: number) => {
    await toggleMetricMutation({metricId, isMnpi: true});
  };

  return {
    metrics: metricsWithAudienceEmployees,
    updateMetricAudience,
    unmarkMetric,
    markMetric,
    isError: isMetricsError || isAudienceEmployeesError,
    isLoading:
      isMetricsLoading ||
      isAudienceEmployeesLoading ||
      isMetricWorkstreamsLoading ||
      isNarrativePillarsLoading,
  };
};
